import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Formik, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { useForgotPassword, useEmailValidation } from './Shared/hooks'
import {
  EmailInput,
  PreLinkButton,
  PostLinkButton,
  OkButton,
  LoginContainer,
  MainContainer,
  FormContainer,
  ForgotPWLogoAndHeaderContainer,
  FormMessage,
  FormHeader,
  FormErrorMessage,
  CheckBox,
  InputContainer,
  FormLabel,
  CommonDiv,
  PassMessageDiv,
  Check,
  PageLogo,
  ForgotPWForm,
  ReturnLoginLink,
  ErrorMark
} from './styles'

type Props = {
  email: string
  error: string
  history: {
    push: (path: string) => void
  }
}

type FormValues = {
  username: string
}

const ForgotPassword = (props: Props) => {
  const { resetSubmitted, forgotPassword } = useForgotPassword()
  const [email, setEmail] = useState<string>('')
  const { emailValidator } = useEmailValidation(email)

  const emailInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
  }

  const renderLinkButton = () => {
    if (emailValidator) {
      return <PostLinkButton type="submit">Send Link</PostLinkButton>
    }
    return <PreLinkButton disabled>Send Link</PreLinkButton>
  }

  return (
    <MainContainer>
      <LoginContainer>
        {!resetSubmitted ? (
          <Formik
            enableReinitialize={true}
            initialValues={{
              username: ''
            }}
            validationSchema={LoginSchema}
            onSubmit={(values: FormValues) => {
              forgotPassword(values)
            }}
            render={({ values, handleBlur, handleChange, handleSubmit, errors, touched }) => (
              <ForgotPWForm onSubmit={handleSubmit}>
                <FormContainer>
                  <CommonDiv>
                    <ForgotPWLogoAndHeaderContainer>
                      <PageLogo src="./assets/SignInLogo.png" alt="HaulR Logo" />
                      <FormHeader> Forgot your password?</FormHeader>
                      <FormMessage>
                        Enter your e-mail address and we’ll send you a link to reset it.
                      </FormMessage>
                    </ForgotPWLogoAndHeaderContainer>
                    <InputContainer>
                      <FormLabel>
                        Email address
                        <EmailInput
                          name="username"
                          label="username"
                          className="loginInputBox"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.username}
                          onInput={(e: React.ChangeEvent<HTMLInputElement>) => emailInputHandler(e)}
                          fieldError={errors.username && touched.username ? true : false}
                        />
                      </FormLabel>
                      <ErrorMessage
                        name="username"
                        render={msg => (
                          <FormErrorMessage>
                            <ErrorMark />
                            {msg}
                          </FormErrorMessage>
                        )}
                      />
                    </InputContainer>
                  </CommonDiv>
                  {renderLinkButton()}
                </FormContainer>
              </ForgotPWForm>
            )}
          />
        ) : (
          <FormContainer>
            <CommonDiv>
              <CheckBox>
                <Check />
              </CheckBox>
              <FormHeader>Link sent</FormHeader>
              <FormMessage>
                We have sent you an e-mail with instructions to create a new password.
              </FormMessage>
              <PassMessageDiv>
                <FormMessage>Can't find it?</FormMessage>
                <FormMessage>1. Check your spam folder</FormMessage>
                <FormMessage>
                  2.{' '}
                  <Link to="/login">
                    <ReturnLoginLink> Return</ReturnLoginLink>
                  </Link>{' '}
                  to login page
                </FormMessage>
              </PassMessageDiv>
            </CommonDiv>
            <OkButton onClick={() => props.history.push('/login')}>OK</OkButton>
          </FormContainer>
        )}
      </LoginContainer>
    </MainContainer>
  )
}

export default ForgotPassword

const LoginSchema = Yup.object().shape({
  username: Yup.string()
    .email('Email address not found')
    .min(3, 'Too Short!')
    .required('Please enter an email address')
})
